/**
 * useTableState Hook
 *
 * Manages all internal table state for the self-contained MetadataTable:
 * - Pagination (page, pageSize)
 * - Sorting (sortKey, sortDirection)
 * - Filtering (user-applied filters)
 * - Search (text search term)
 *
 * This hook encapsulates state that was previously managed by parent pages,
 * making the table component truly self-contained.
 */

import { useState, useCallback } from 'react'
import type { Filter, SortDirection } from '@/types/api.types'
import type { UseTableStateResult, TableInternalState } from '../types/selfContained.types'

/**
 * Default initial state
 */
const DEFAULT_STATE: TableInternalState = {
  page: 0,
  pageSize: 50,
  sortKey: null,
  sortDirection: null,
  filters: [],
  searchTerm: '',
}

/**
 * Options for useTableState hook
 */
interface UseTableStateOptions {
  /** Initial page (0-indexed) */
  initialPage?: number
  /** Initial page size */
  initialPageSize?: number
  /** Initial sort key */
  initialSortKey?: string | null
  /** Initial sort direction */
  initialSortDirection?: SortDirection | null
  /** Initial filters */
  initialFilters?: Filter[]
  /** Initial search term */
  initialSearchTerm?: string
}

/**
 * Hook to manage all table state internally.
 *
 * @param options - Optional initial state configuration
 * @returns State values and setters
 *
 * @example Basic usage
 * ```tsx
 * const {
 *   page,
 *   pageSize,
 *   sortKey,
 *   sortDirection,
 *   filters,
 *   searchTerm,
 *   setPage,
 *   handleSort,
 *   setFilters,
 *   setSearchTerm,
 * } = useTableState()
 * ```
 *
 * @example With initial values
 * ```tsx
 * const state = useTableState({
 *   initialPageSize: 25,
 *   initialSortKey: 'name',
 *   initialSortDirection: 'asc',
 * })
 * ```
 */
export function useTableState(options: UseTableStateOptions = {}): UseTableStateResult {
  // Initialize state with options or defaults
  const [page, setPageRaw] = useState(options.initialPage ?? DEFAULT_STATE.page)
  const [pageSize, setPageSizeRaw] = useState(options.initialPageSize ?? DEFAULT_STATE.pageSize)
  const [sortKey, setSortKey] = useState<string | null>(
    options.initialSortKey ?? DEFAULT_STATE.sortKey
  )
  const [sortDirection, setSortDirection] = useState<SortDirection | null>(
    options.initialSortDirection ?? DEFAULT_STATE.sortDirection
  )
  const [filters, setFiltersRaw] = useState<Filter[]>(
    options.initialFilters ?? DEFAULT_STATE.filters
  )
  const [searchTerm, setSearchTermRaw] = useState(
    options.initialSearchTerm ?? DEFAULT_STATE.searchTerm
  )

  // Reset pagination to first page
  const resetPagination = useCallback(() => {
    setPageRaw(0)
  }, [])

  // Set page (with validation)
  const setPage = useCallback((newPage: number) => {
    setPageRaw(Math.max(0, newPage))
  }, [])

  // Set page size (resets to first page)
  const setPageSize = useCallback((newSize: number) => {
    setPageSizeRaw(Math.max(1, newSize))
    setPageRaw(0) // Reset to first page when page size changes
  }, [])

  // Handle sort (toggle direction on same key, reset to asc on new key)
  const handleSort = useCallback((key: string) => {
    if (sortKey === key) {
      // Same key - toggle direction or clear
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        // Clear sort on third click
        setSortKey(null)
        setSortDirection(null)
      } else {
        setSortDirection('asc')
      }
    } else {
      // New key - set ascending
      setSortKey(key)
      setSortDirection('asc')
    }
    // Reset to first page on sort change
    setPageRaw(0)
  }, [sortKey, sortDirection])

  // Set filters (resets pagination)
  const setFilters = useCallback((newFilters: Filter[]) => {
    setFiltersRaw(newFilters)
    setPageRaw(0)
  }, [])

  // Add a filter (resets pagination)
  const addFilter = useCallback((filter: Filter) => {
    setFiltersRaw((prev) => {
      // Replace if same key exists, otherwise add
      const existing = prev.findIndex((f) => f.key === filter.key)
      if (existing >= 0) {
        const updated = [...prev]
        updated[existing] = filter
        return updated
      }
      return [...prev, filter]
    })
    setPageRaw(0)
  }, [])

  // Remove a filter by key (resets pagination)
  const removeFilter = useCallback((key: string) => {
    setFiltersRaw((prev) => prev.filter((f) => f.key !== key))
    setPageRaw(0)
  }, [])

  // Set search term (resets pagination)
  const setSearchTerm = useCallback((term: string) => {
    setSearchTermRaw(term)
    setPageRaw(0)
  }, [])

  // Reset all state to defaults
  const resetAll = useCallback(() => {
    setPageRaw(DEFAULT_STATE.page)
    setPageSizeRaw(DEFAULT_STATE.pageSize)
    setSortKey(DEFAULT_STATE.sortKey)
    setSortDirection(DEFAULT_STATE.sortDirection)
    setFiltersRaw(DEFAULT_STATE.filters)
    setSearchTermRaw(DEFAULT_STATE.searchTerm)
  }, [])

  return {
    // State values
    page,
    pageSize,
    sortKey,
    sortDirection,
    filters,
    searchTerm,

    // State setters
    setPage,
    setPageSize,
    handleSort,
    setFilters,
    addFilter,
    removeFilter,
    setSearchTerm,

    // Utilities
    resetAll,
    resetPagination,
  }
}
